import React, { useState, useEffect, useMemo } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const TEAMS = ['WV Waxers', 'WV Nail Team', 'Lonsdale', 'Mountain Hwy'];

const CENTER_NAMES = {
  // Map centerId to friendly name — will use centerId as fallback
};

function StarRating({ rating }) {
  return (
    <span style={{ fontSize: 16, letterSpacing: 1 }}>
      {[1, 2, 3, 4, 5].map(i => (
        <span key={i} style={{ color: i <= rating ? '#f5a623' : '#ddd' }}>★</span>
      ))}
    </span>
  );
}

function AdminReviews() {
  const [tab, setTab] = useState('pending'); // pending | assigned
  const [reviews, setReviews] = useState([]);
  const [assignedReviews, setAssignedReviews] = useState([]);
  const [therapists, setTherapists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Per-review state
  const [selections, setSelections] = useState({}); // { reviewId: { therapistId, teamReview } }
  const [expandedComments, setExpandedComments] = useState({});
  const [assigning, setAssigning] = useState({}); // { reviewId: true } while saving
  const [therapistSearch, setTherapistSearch] = useState({});

  useEffect(() => {
    loadQueue();
    loadAssigned();
  }, []);

  async function loadQueue() {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/admin/review-queue`);
      const data = await res.json();
      setReviews(data.reviews || []);
      setTherapists(data.therapists || []);

      // Pre-populate selections with auto-suggestions
      const initial = {};
      for (const r of (data.reviews || [])) {
        if (r.suggestedTherapistId) {
          initial[r.id] = { therapistId: r.suggestedTherapistId, teamReview: '' };
        }
      }
      setSelections(prev => ({ ...initial, ...prev }));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function loadAssigned() {
    try {
      const res = await fetch(`${API_URL}/api/admin/reviews?matched=true&limit=200`);
      const data = await res.json();
      // Only show admin-assigned ones (have assignedAt or matchMethod = admin_assigned)
      setAssignedReviews(data.reviews || []);
    } catch (err) {
      console.error('Failed to load assigned reviews:', err);
    }
  }

  async function handleAssign(reviewId) {
    const sel = selections[reviewId];
    if (!sel?.therapistId) return;

    setAssigning(prev => ({ ...prev, [reviewId]: true }));
    try {
      const res = await fetch(`${API_URL}/api/admin/review-assign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          reviewId,
          therapistId: sel.therapistId,
          teamReview: sel.teamReview || null,
          assignedBy: 'Riley'
        })
      });
      if (!res.ok) throw new Error('Assign failed');

      // Remove from pending, refresh assigned
      setReviews(prev => prev.filter(r => r.id !== reviewId));
      loadAssigned();
    } catch (err) {
      alert('Failed to assign: ' + err.message);
    } finally {
      setAssigning(prev => ({ ...prev, [reviewId]: false }));
    }
  }

  async function handleUnassign(reviewId) {
    if (!window.confirm('Remove assignment and send back to queue?')) return;

    try {
      const res = await fetch(`${API_URL}/api/admin/review-unassign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reviewId })
      });
      if (!res.ok) throw new Error('Unassign failed');

      setAssignedReviews(prev => prev.filter(r => r.id !== reviewId));
      loadQueue();
    } catch (err) {
      alert('Failed to unassign: ' + err.message);
    }
  }

  function updateSelection(reviewId, field, value) {
    setSelections(prev => ({
      ...prev,
      [reviewId]: { ...(prev[reviewId] || {}), [field]: value }
    }));
  }

  // Group therapists by center for dropdown
  const therapistsByCenter = useMemo(() => {
    const grouped = {};
    for (const t of therapists) {
      const key = t.centerId || 'Unknown';
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(t);
    }
    return grouped;
  }, [therapists]);

  function getFilteredTherapists(reviewId) {
    const search = (therapistSearch[reviewId] || '').toLowerCase();
    if (!search) return therapists;
    return therapists.filter(t =>
      t.displayName.toLowerCase().includes(search) ||
      t.firstName.toLowerCase().includes(search)
    );
  }

  function formatDate(dateStr) {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }

  function truncateComment(text, maxLen = 150) {
    if (!text || text.length <= maxLen) return text;
    return text.substring(0, maxLen) + '...';
  }

  const pendingCount = reviews.length;

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Review Queue</h1>
        <span style={styles.badge}>{pendingCount} pending</span>
      </div>

      {/* Tabs */}
      <div style={styles.tabs}>
        <button
          style={{ ...styles.tab, ...(tab === 'pending' ? styles.tabActive : {}) }}
          onClick={() => setTab('pending')}
        >
          Pending ({pendingCount})
        </button>
        <button
          style={{ ...styles.tab, ...(tab === 'assigned' ? styles.tabActive : {}) }}
          onClick={() => setTab('assigned')}
        >
          Assigned ({assignedReviews.length})
        </button>
      </div>

      {error && <div style={styles.error}>Error: {error}</div>}
      {loading && <div style={styles.loading}>Loading reviews...</div>}

      {/* Pending Tab */}
      {tab === 'pending' && !loading && (
        <div style={styles.cardList}>
          {reviews.length === 0 && (
            <div style={styles.empty}>All reviews have been assigned. Nice work!</div>
          )}
          {reviews.map(review => {
            const sel = selections[review.id] || {};
            const isExpanded = expandedComments[review.id];
            const isSaving = assigning[review.id];
            const hasSuggestion = !!review.suggestedTherapistId;
            const suggestedTherapist = hasSuggestion
              ? therapists.find(t => t.id === review.suggestedTherapistId)
              : null;
            const filtered = getFilteredTherapists(review.id);
            const filteredByCenter = {};
            for (const t of filtered) {
              const key = t.centerId || 'Unknown';
              if (!filteredByCenter[key]) filteredByCenter[key] = [];
              filteredByCenter[key].push(t);
            }

            return (
              <div key={review.id} style={styles.card}>
                <div style={styles.cardTop}>
                  <div style={styles.cardMeta}>
                    <span style={styles.reviewerName}>{review.reviewerName}</span>
                    <StarRating rating={review.starRating} />
                    <span style={styles.dateText}>{formatDate(review.reviewDate)}</span>
                  </div>
                  <span style={styles.centerBadge}>{review.centerId?.substring(0, 8) || 'Unknown'}</span>
                </div>

                {/* Comment */}
                {review.comment && (
                  <div style={styles.commentSection}>
                    <p style={styles.commentText}>
                      {isExpanded ? review.comment : truncateComment(review.comment)}
                    </p>
                    {review.comment.length > 150 && (
                      <button
                        style={styles.expandBtn}
                        onClick={() => setExpandedComments(prev => ({ ...prev, [review.id]: !isExpanded }))}
                      >
                        {isExpanded ? 'Show less' : 'Show more'}
                      </button>
                    )}
                  </div>
                )}

                {/* Mentioned staff badges */}
                {review.mentionedStaff && review.mentionedStaff.length > 0 && (
                  <div style={styles.mentionedRow}>
                    <span style={styles.mentionedLabel}>Mentioned:</span>
                    {review.mentionedStaff.map((name, i) => (
                      <span key={i} style={styles.mentionBadge}>{name}</span>
                    ))}
                  </div>
                )}

                {/* Auto-suggestion highlight */}
                {suggestedTherapist && (
                  <div style={styles.suggestionBanner}>
                    Auto-suggested: <strong>{suggestedTherapist.displayName}</strong> (name found in comment)
                  </div>
                )}

                {/* Assignment controls */}
                <div style={styles.assignRow}>
                  {/* Therapist search + select */}
                  <div style={styles.selectGroup}>
                    <label style={styles.selectLabel}>Therapist</label>
                    <input
                      type="text"
                      placeholder="Search therapists..."
                      value={therapistSearch[review.id] || ''}
                      onChange={e => setTherapistSearch(prev => ({ ...prev, [review.id]: e.target.value }))}
                      style={styles.searchInput}
                    />
                    <select
                      value={sel.therapistId || ''}
                      onChange={e => updateSelection(review.id, 'therapistId', e.target.value)}
                      style={{
                        ...styles.select,
                        ...(hasSuggestion && sel.therapistId === review.suggestedTherapistId ? styles.suggestedSelect : {})
                      }}
                    >
                      <option value="">-- Select --</option>
                      {Object.entries(filteredByCenter).map(([centerId, cenTherapists]) => (
                        <optgroup key={centerId} label={centerId.substring(0, 12)}>
                          {cenTherapists.map(t => (
                            <option key={t.id} value={t.id}>
                              {t.displayName}
                              {t.id === review.suggestedTherapistId ? ' (suggested)' : ''}
                            </option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                  </div>

                  {/* Team dropdown */}
                  <div style={styles.selectGroup}>
                    <label style={styles.selectLabel}>Team</label>
                    <select
                      value={sel.teamReview || ''}
                      onChange={e => updateSelection(review.id, 'teamReview', e.target.value)}
                      style={styles.select}
                    >
                      <option value="">-- Optional --</option>
                      {TEAMS.map(t => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </div>

                  <button
                    style={{
                      ...styles.assignBtn,
                      ...((!sel.therapistId || isSaving) ? styles.assignBtnDisabled : {})
                    }}
                    disabled={!sel.therapistId || isSaving}
                    onClick={() => handleAssign(review.id)}
                  >
                    {isSaving ? 'Assigning...' : 'Assign'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Assigned Tab */}
      {tab === 'assigned' && (
        <div style={styles.cardList}>
          {assignedReviews.length === 0 && (
            <div style={styles.empty}>No assigned reviews yet.</div>
          )}
          {assignedReviews.map(review => (
            <div key={review.id} style={styles.card}>
              <div style={styles.cardTop}>
                <div style={styles.cardMeta}>
                  <span style={styles.reviewerName}>{review.reviewerName}</span>
                  <StarRating rating={review.starRating} />
                  <span style={styles.dateText}>{formatDate(review.reviewDate)}</span>
                </div>
                <span style={styles.centerBadge}>{review.centerId?.substring(0, 8) || 'Unknown'}</span>
              </div>

              {review.comment && (
                <p style={styles.commentText}>
                  {expandedComments[review.id] ? review.comment : truncateComment(review.comment)}
                  {review.comment.length > 150 && (
                    <button
                      style={styles.expandBtn}
                      onClick={() => setExpandedComments(prev => ({ ...prev, [review.id]: !expandedComments[review.id] }))}
                    >
                      {expandedComments[review.id] ? 'Show less' : 'Show more'}
                    </button>
                  )}
                </p>
              )}

              <div style={styles.assignedInfo}>
                <span style={styles.assignedLabel}>
                  Assigned to: <strong>{review.appointmentTherapistName}</strong>
                </span>
                {review.teamReview && (
                  <span style={styles.teamBadge}>{review.teamReview}</span>
                )}
                {review.matchMethod === 'admin_assigned' && (
                  <span style={styles.methodBadge}>Admin assigned</span>
                )}
                <button
                  style={styles.unassignBtn}
                  onClick={() => handleUnassign(review.id)}
                >
                  Unassign
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    maxWidth: 900,
    margin: '0 auto',
    padding: '24px 16px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    backgroundColor: '#fff',
    minHeight: '100vh'
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 20
  },
  title: {
    fontSize: 24,
    fontWeight: 700,
    color: '#1a1a1a',
    margin: 0
  },
  badge: {
    backgroundColor: '#e91e8c',
    color: '#fff',
    fontSize: 13,
    fontWeight: 600,
    padding: '4px 10px',
    borderRadius: 12
  },
  tabs: {
    display: 'flex',
    gap: 0,
    marginBottom: 20,
    borderBottom: '2px solid #eee'
  },
  tab: {
    padding: '10px 24px',
    fontSize: 14,
    fontWeight: 600,
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    color: '#888',
    borderBottom: '2px solid transparent',
    marginBottom: -2,
    transition: 'color 0.2s, border-color 0.2s'
  },
  tabActive: {
    color: '#e91e8c',
    borderBottomColor: '#e91e8c'
  },
  error: {
    background: '#fff0f0',
    color: '#c00',
    padding: 12,
    borderRadius: 8,
    marginBottom: 16,
    fontSize: 14
  },
  loading: {
    textAlign: 'center',
    padding: 40,
    color: '#888',
    fontSize: 15
  },
  empty: {
    textAlign: 'center',
    padding: 60,
    color: '#aaa',
    fontSize: 15
  },
  cardList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12
  },
  card: {
    border: '1px solid #e8e8e8',
    borderRadius: 10,
    padding: 16,
    background: '#fafafa',
    transition: 'box-shadow 0.2s',
  },
  cardTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8
  },
  cardMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    flexWrap: 'wrap'
  },
  reviewerName: {
    fontWeight: 600,
    fontSize: 15,
    color: '#222'
  },
  dateText: {
    fontSize: 13,
    color: '#999'
  },
  centerBadge: {
    fontSize: 11,
    color: '#666',
    background: '#eee',
    padding: '3px 8px',
    borderRadius: 6,
    fontFamily: 'monospace'
  },
  commentSection: {
    marginBottom: 10
  },
  commentText: {
    fontSize: 14,
    color: '#444',
    lineHeight: 1.5,
    margin: '4px 0'
  },
  expandBtn: {
    background: 'none',
    border: 'none',
    color: '#e91e8c',
    fontSize: 13,
    cursor: 'pointer',
    padding: 0,
    fontWeight: 500
  },
  mentionedRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    marginBottom: 10,
    flexWrap: 'wrap'
  },
  mentionedLabel: {
    fontSize: 12,
    color: '#888',
    fontWeight: 500
  },
  mentionBadge: {
    fontSize: 12,
    fontWeight: 600,
    color: '#e91e8c',
    background: '#fce4f2',
    padding: '2px 8px',
    borderRadius: 10
  },
  suggestionBanner: {
    background: '#f0f9ff',
    border: '1px solid #bde0fe',
    borderRadius: 6,
    padding: '6px 10px',
    fontSize: 13,
    color: '#1a6db0',
    marginBottom: 10
  },
  assignRow: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: 12,
    flexWrap: 'wrap',
    marginTop: 8
  },
  selectGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    flex: '1 1 180px',
    minWidth: 160
  },
  selectLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: '#888',
    textTransform: 'uppercase',
    letterSpacing: 0.5
  },
  searchInput: {
    padding: '6px 8px',
    fontSize: 13,
    border: '1px solid #ddd',
    borderRadius: 6,
    outline: 'none',
  },
  select: {
    padding: '7px 8px',
    fontSize: 13,
    border: '1px solid #ddd',
    borderRadius: 6,
    background: '#fff',
    outline: 'none',
    cursor: 'pointer'
  },
  suggestedSelect: {
    borderColor: '#bde0fe',
    background: '#f0f9ff'
  },
  assignBtn: {
    padding: '8px 20px',
    fontSize: 14,
    fontWeight: 600,
    color: '#fff',
    backgroundColor: '#e91e8c',
    border: 'none',
    borderRadius: 8,
    cursor: 'pointer',
    transition: 'background 0.2s',
    whiteSpace: 'nowrap',
    alignSelf: 'flex-end'
  },
  assignBtnDisabled: {
    backgroundColor: '#ccc',
    cursor: 'not-allowed'
  },
  assignedInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    marginTop: 8,
    flexWrap: 'wrap'
  },
  assignedLabel: {
    fontSize: 14,
    color: '#333'
  },
  teamBadge: {
    fontSize: 12,
    fontWeight: 500,
    color: '#5a5a5a',
    background: '#e8e8e8',
    padding: '2px 8px',
    borderRadius: 10
  },
  methodBadge: {
    fontSize: 11,
    color: '#888',
    fontStyle: 'italic'
  },
  unassignBtn: {
    padding: '4px 12px',
    fontSize: 12,
    color: '#c00',
    background: 'none',
    border: '1px solid #c00',
    borderRadius: 6,
    cursor: 'pointer',
    marginLeft: 'auto'
  }
};

export default AdminReviews;
